import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Cloud,
  Server,
  Database,
  Network,
  Shield,
  Zap,
  Settings,
  FileText,
} from "lucide-react";
import TerraformIcon from "./TerraformIcon";

interface TerraformOperation {
  id: string;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  color: string;
  bgColor: string;
}

const terraformOperations: TerraformOperation[] = [
  {
    id: "analyzing",
    icon: Settings,
    title: "Analyzing Infrastructure",
    description:
      "Parsing your requirements and determining optimal resource configuration",
    color: "text-gray-700",
    bgColor: "bg-gray-50",
  },
  {
    id: "planning",
    icon: FileText,
    title: "Generating Terraform Plan",
    description: "Creating infrastructure blueprint with resource dependencies",
    color: "text-gray-700",
    bgColor: "bg-gray-50",
  },
  {
    id: "resources",
    icon: Cloud,
    title: "Configuring Cloud Resources",
    description: "Setting up compute, storage, and networking components",
    color: "text-gray-700",
    bgColor: "bg-gray-50",
  },
  {
    id: "security",
    icon: Shield,
    title: "Applying Security Policies",
    description: "Implementing IAM roles, security groups, and access controls",
    color: "text-gray-700",
    bgColor: "bg-gray-50",
  },
  {
    id: "networking",
    icon: Network,
    title: "Building Network Architecture",
    description: "Establishing VPCs, subnets, and connectivity patterns",
    color: "text-gray-700",
    bgColor: "bg-gray-50",
  },
  {
    id: "optimization",
    icon: Zap,
    title: "Optimizing Configuration",
    description: "Fine-tuning performance, cost, and scalability settings",
    color: "text-gray-700",
    bgColor: "bg-gray-50",
  },
];

interface TerraformPreviewSpinnerProps {
  className?: string;
  duration?: number;
}

export const TerraformPreviewSpinner: React.FC<
  TerraformPreviewSpinnerProps
> = ({ className = "", duration = 3000 }) => {
  const [currentOperationIndex, setCurrentOperationIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentOperationIndex((prev) => {
        const next = (prev + 1) % terraformOperations.length;
        return next;
      });
    }, duration);

    return () => clearInterval(interval);
  }, [duration]);

  const currentOperation = terraformOperations[currentOperationIndex];
  const IconComponent = currentOperation.icon;

  return (
    <div
      className={`flex flex-col items-center justify-center space-y-8 p-8 ${className}`}
    >
      {/* Current Operation Display */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentOperation.id}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.3 }}
          className="text-center space-y-4 max-w-md"
        >
          {/* Operation Icon and Title */}
          <div className="flex items-center justify-center space-x-3">
            <div
              className={`p-2 rounded-lg ${currentOperation.bgColor} border border-gray-200`}
            >
              <IconComponent className={`w-5 h-5 ${currentOperation.color}`} />
            </div>
            <h3 className="text-lg font-medium text-gray-800">
              {currentOperation.title}
            </h3>
          </div>

          {/* Operation Description */}
          <p className="text-sm text-gray-600 leading-relaxed">
            {currentOperation.description}
          </p>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Compact version for smaller spaces
interface CompactTerraformSpinnerProps {
  className?: string;
  text?: string;
}

export const CompactTerraformSpinner: React.FC<
  CompactTerraformSpinnerProps
> = ({ className = "", text = "Generating Terraform modules" }) => {
  return (
    <div className={`flex items-center justify-center space-x-3 ${className}`}>
      {/* Text with subtle animation */}
      <div className="flex flex-col">
        <motion.p
          className="text-sm font-medium text-gray-700"
          animate={{ opacity: [0.8, 1, 0.8] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          {text}
        </motion.p>
        <p className="text-xs text-gray-500">This may take a few moments...</p>
      </div>

      {/* Small orbital indicators */}
      <div className="relative w-6 h-6">
        {[0, 1, 2].map((index) => (
          <motion.div
            key={index}
            className="absolute w-1 h-1 bg-green-500 rounded-full"
            animate={{
              rotate: [0, 360],
              x: [0, 8 * Math.cos((index * 2 * Math.PI) / 3), 0],
              y: [0, 8 * Math.sin((index * 2 * Math.PI) / 3), 0],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "linear",
              delay: index * 0.2,
            }}
            style={{
              left: "50%",
              top: "50%",
              marginLeft: "-2px",
              marginTop: "-2px",
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default TerraformPreviewSpinner;
